// GRWM's draft as the form keeps it (spec G14-grwm, PR D; a form with no live equivalent,
// GRWM-13): the server holds the member's draft (`.../templates/grwm/draft`), so it survives the
// panel; the form reads it, keeps its edits there, and asks the server's script director and
// stills. As Pixar Talking Ad's (`pixar-draft.ts`), with one approval before the video and no
// kept takes (GRWM-12).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetGrwmDraftQueryKey,
  useApproveGrwmScript,
  useGetGrwmDraft,
  useRegenerateGrwmStill,
  useResetGrwmDraft,
  useWriteGrwmDraft,
  useWriteGrwmScript,
  writeGrwmDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type { GrwmDraft, GrwmDraftStill, GrwmScript, GrwmStillName } from "../../../api/generated/model";
import {
  GrwmApproval,
  GrwmDraftWrite,
  GrwmInputs,
  GrwmScriptAsk,
  type GrwmDraftWriteOutput,
  type GrwmInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface GrwmForm {
  inputs: GrwmInputsOutput;
  script: GrwmScript | null;
}

/** The video's settings the footer keeps open once the script is approved (neither changes a
 * still): the form's own until "Generate video" sends them. */
export interface VideoSettings {
  resolution: GrwmInputsOutput["resolution"];
  stairs: boolean;
}

/**
 * A still under way: drawing, or pending while another draws (the server starts each still as
 * its sources land, so with nothing drawing a pending still waits on one that failed).
 */
export function stillWaiting(name: GrwmStillName, stills: readonly GrwmDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  return status === "pending" && stills.some((each) => each.status === "working");
}

/** "Generate video"'s body: the run the drawn draft gives (`GrwmDraft.video`, GRWM-18) with the
 * footer's settings; null until every still is drawn. */
export function videoBody(draft: GrwmDraft, settings: VideoSettings) {
  if (draft.video === undefined || draft.video === null) return null;
  return { type: "video.template" as const, inputs: { ...draft.video, ...settings } };
}

/** The draft refetched while it works: every 2 s while the director or a still works, and a
 * moment after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** Where the draft's work stands, as the progress line names it. */
export interface DraftWork {
  writing: boolean;
  drawing: boolean;
}

export function workOf(draft: GrwmDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    writing: draft.script_director === "working" || answering,
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills)),
  };
}

function working(work: DraftWork): boolean {
  return work.writing || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: GrwmDraft): GrwmForm {
  const inputs = GrwmInputs.safeParse(draft.inputs);
  return { inputs: inputs.success ? inputs.data : GrwmInputs.parse({}), script: draft.script };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function useGrwmDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetGrwmDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<GrwmForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetGrwmDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: GrwmDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteGrwmScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveGrwmScript(asks);
  const regeneration = useRegenerateGrwmStill(asks);
  const reset = useResetGrwmDraft(asks);
  const asking = script.isPending || approval.isPending || regeneration.isPending || reset.isPending;
  const work: DraftWork = draft === undefined ? { writing: false, drawing: false } : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteGrwmDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: GrwmDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) writeGrwmDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = GrwmDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    /** The draft read again now: after "Generate video". */
    refresh: refetch,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<GrwmForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<GrwmInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = GrwmScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = GrwmApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: GrwmStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
